import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { GoPlus } from "react-icons/go";
import { Table } from '../../../Components/Table/table.component';
import { useNavigate } from 'react-router-dom';
import { useListCollaborators } from './collaborators-list.hook';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { ListCollaboratorsUseCase } from '../../../Usecase/Collaborators/list-collaborators-usecase';
import { ActionButtons } from '../../../Components/Form/ActionButtons/action-buttons.component';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { DestroyCollaboratorsUseCase } from '../../../Usecase/Collaborators/destroy-collaborators-usecase';
import { ShowCollaboratorsUseCase } from '../../../Usecase/Collaborators/show-collaborators-usecase';
import { Can } from '../../../Components/Can';
import { CollaboratorPermissionsCell } from '../../../Components/Collaborators/CollaboratorPermissionsCell/collaborator-permissions-cell.component';

interface ICollaboratorsList {
  listCollaboratorsUseCase: ListCollaboratorsUseCase
  destroyCollaboratorsUseCase: DestroyCollaboratorsUseCase
  showCollaboratorsUseCase: ShowCollaboratorsUseCase
}

const CellText: FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() || '—'

  return (
    <Tooltip title={text !== '—' ? text : ''} placement="top-start" enterDelay={400}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.25,
          fontSize: '0.8125rem',
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  )
}

const CollaboratorsList : FC<ICollaboratorsList> = ({ listCollaboratorsUseCase, destroyCollaboratorsUseCase, showCollaboratorsUseCase }) => {
    const navigate = useNavigate()

    const {
      listCollaborators,
      list,
      loading,
      loadError,
      setCollaboratorToDestroy,
      collaboratorToDestroy,
      collaboratorToDestroyName,
      handleDestroyCollaborator,
      totalRegisters,
      handleSearch,
      handlePageChange,
      currentSearch,
    } = useListCollaborators({
      listCollaboratorsUseCase,
      destroyCollaboratorsUseCase,
      showCollaboratorsUseCase,
    })

    useEffect(() => {
      listCollaborators({ page: 1, search: '' })
    }, [listCollaborators])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'name',
            headerName: 'Colaborador',
            flex: 1,
            minWidth: 140,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.name} />,
          },
          {
            field: 'username',
            headerName: 'E-mail',
            flex: 1,
            minWidth: 180,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.username} />,
          },
          {
            field: 'permissionsScreens',
            headerName: 'Permissões',
            flex: 1.4,
            minWidth: 220,
            sortable: false,
            renderCell: (params: any) => (
              <CollaboratorPermissionsCell
                permissionsScreens={params.row?.permissionsScreens}
                permissions={params.row?.permissions}
              />
            ),
          },
          {
            field: "actions",
            headerName: "Ações",
            headerAlign: "center",
            align: "center",
            width: 72,
            sortable: false,
            renderCell: (params: any) => (
              <ActionButtons
                params={params ?? {}}
                setItemToDestroy={setCollaboratorToDestroy}
                editFunction={() => navigate(`/collaborators-form/${params?.row?.id}`)}
              />
            )
          }
        ];
      
        return createColumns;
      }, [navigate, setCollaboratorToDestroy]);

    return (
      <Can permissions={['menu:collaborators:manage']}>
        <BaseScreen>
        <ConfirmDialog
          title='Remover Colaborador'
          onCancel={() => setCollaboratorToDestroy('')}
          description={`Tem certeza que deseja remover "${collaboratorToDestroyName}"?`}
          open={!!collaboratorToDestroy}
          onConfirm={handleDestroyCollaborator}
        />
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage 
                    title='Colaboradores'
                    subtitle='Gerencie os colaboradores com acesso ao HUB'
                    breadcrumb={[{ title: 'Colaboradores', link: "/collaborators-list"}, { title: 'Listagem'}]}
                    minHeight="auto"
                    search
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch='Pesquisar por nome ou e-mail'
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/collaborators-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        columns={columns}
                        totalCount={totalRegisters}
                        search={currentSearch}
                        onPageChange={handlePageChange}
                        sortingMode="client"
                        rowHeight={52}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={52} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar os colaboradores."
                      onRetry={() => listCollaborators({ page: 1, search: currentSearch })}
                    />
                  ) : (
                    <Placeholder description="Nenhum colaborador encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { CollaboratorsList };
